import React, { useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Link, router } from '@inertiajs/react';
import { Plus, Edit, Trash2, Calendar, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';

type Event = {
  id: number;
  title: string;
  description: string;
  event_date: string;
  registration_url: string;
  position: number;
  published: boolean;
};

type Props = {
  events: Event[];
};

export default function EventsIndex({ events }: Props) {
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const handleDelete = () => {
    if (deleteId) {
      router.delete(`/admin/events/${deleteId}`, {
        onSuccess: () => setDeleteId(null),
      });
    }
  };

  const upcomingEvents = events.filter(event => new Date(event.event_date) >= new Date());
  const pastEvents = events.filter(event => new Date(event.event_date) < new Date());

  const EventCard = ({ event }: { event: Event }) => (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-medium text-gray-900">{event.title}</h3>
          <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
            <Calendar className="h-4 w-4" />
            <span>{format(new Date(event.event_date), 'PPP p')}</span>
          </div>
        </div>
        <Badge variant={event.published ? "default" : "secondary"}>
          {event.published ? "Published" : "Draft"}
        </Badge>
      </div>
      
      {event.description && (
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
          {event.description}
        </p>
      )}

      {event.registration_url && (
        <div className="flex items-center gap-2 mb-4">
          <ExternalLink className="h-4 w-4 text-gray-400" />
          <a 
            href={event.registration_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-primary hover:underline"
          >
            Registration Link
          </a>
        </div>
      )}

      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          asChild
        >
          <Link href={`/admin/events/${event.id}/edit`}>
            <Edit className="h-3 w-3 mr-1" />
            Edit
          </Link>
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={() => setDeleteId(event.id)}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );

  return (
    <AdminLayout title="Community Events">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Community Events</h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage upcoming and past community events
            </p>
          </div>
          <Button asChild>
            <Link href="/admin/events/new">
              <Plus className="h-4 w-4 mr-2" />
              Add Event
            </Link>
          </Button>
        </div>

        {/* Upcoming Events */}
        {upcomingEvents.length > 0 && (
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Upcoming Events</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {upcomingEvents.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          </div>
        )}

        {/* Past Events */}
        {pastEvents.length > 0 && (
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Past Events</h2>
            <div className="grid gap-4 md:grid-cols-2 opacity-75">
              {pastEvents.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {events.length === 0 && (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-gray-500">No events found. Create your first event to get started.</p>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Event</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this event? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}